/**
 * Helper to determine if a specific settings sub-option is visible/allowed for a tenant.
 *
 * @param {string} settingKey - e.g. "setting_store_details", "setting_rooms", etc.
 * @param {string|null} parentModuleKey - e.g. "pms", "pos", "kitchen", "rfid_doorlock"
 * @param {string|null} modulesStr - comma-separated list of enabled modules/settings from storeSettings or user
 * @returns {boolean}
 */
export function isSettingVisible(settingKey, parentModuleKey = null, modulesStr = null) {
  if (modulesStr === undefined || modulesStr === null) {
    return true; // Default visible if not loaded or not configured
  }

  const enabledList = modulesStr.split(",").map((m) => m.trim()).filter(Boolean);

  // 1. Parent module dependency check
  if (parentModuleKey) {
    if (parentModuleKey === "rfid_doorlock") {
      const hasRfid = enabledList.includes("rfid_doorlock") || enabledList.includes("rfid");
      if (!hasRfid) return false;
    } else if (!enabledList.includes(parentModuleKey)) {
      return false;
    }
  }

  if (settingKey === "rfid_doorlock") {
    return enabledList.includes("rfid_doorlock") || enabledList.includes("rfid");
  }

  // 2. Backward compatibility: If no "setting_*" keys exist in enabled_modules,
  // the tenant has not yet had granular settings configured by Super Admin, so default to visible.
  const hasAnySettingKey = enabledList.some((k) => k.startsWith("setting_"));
  if (!hasAnySettingKey) {
    return true;
  }

  // 3. Explicit check for setting key
  return enabledList.includes(settingKey);
}

/**
 * Settings configuration map with route paths, setting keys, parent modules, and labels.
 */
export const SETTINGS_CONFIG_MAP = [
  {
    path: "/dashboard/settings",
    exact: true,
    settingKey: "setting_store_details",
    parentModule: null,
    title: "Store Details",
  },
  {
    path: "/dashboard/settings/details",
    exact: true,
    settingKey: "setting_store_details",
    parentModule: null,
    title: "Store Details",
  },
  {
    path: "/dashboard/settings/store-details",
    exact: true,
    settingKey: "setting_store_details",
    parentModule: null,
    title: "Store Details",
  },
  {
    path: "/dashboard/settings/rfid-doorlock",
    exact: false,
    settingKey: "rfid_doorlock",
    parentModule: "rfid_doorlock",
    title: "RFID Door Lock Integration",
  },
  {
    path: "/dashboard/settings/kitchen-displays",
    exact: false,
    settingKey: "setting_kitchen_displays",
    parentModule: "kitchen",
    title: "Kitchen Displays",
  },
  {
    path: "/dashboard/settings/branches",
    exact: false,
    settingKey: "setting_branches",
    parentModule: null,
    title: "Branches Management",
  },
  {
    path: "/dashboard/settings/workflow",
    exact: false,
    settingKey: "setting_workflow",
    parentModule: null,
    title: "Workflow & Sync",
  },
  {
    path: "/dashboard/settings/application-workflow",
    exact: false,
    settingKey: "setting_workflow",
    parentModule: null,
    title: "Workflow & Sync",
  },
  {
    path: "/dashboard/settings/sync",
    exact: false,
    settingKey: "setting_workflow",
    parentModule: null,
    title: "Workflow & Sync",
  },
  {
    path: "/dashboard/settings/print-settings",
    exact: false,
    settingKey: "setting_print",
    parentModule: null,
    title: "Print Settings",
  },
  {
    path: "/dashboard/settings/tables",
    exact: false,
    settingKey: "setting_tables",
    parentModule: "pos",
    title: "Tables Configuration",
  },
  {
    path: "/dashboard/settings/rooms",
    exact: false,
    settingKey: "setting_rooms",
    parentModule: "pms",
    title: "Rooms Settings",
  },
  {
    path: "/dashboard/settings/menu-items",
    exact: false,
    settingKey: "setting_menu_items",
    parentModule: null,
    title: "Menu Items & Categories",
  },
  {
    path: "/dashboard/settings/tax-setup",
    exact: false,
    settingKey: "setting_tax",
    parentModule: null,
    title: "Tax Setup",
  },
  {
    path: "/dashboard/settings/payment-types",
    exact: false,
    settingKey: "setting_payment_types",
    parentModule: null,
    title: "Payment Types",
  },
  {
    path: "/dashboard/settings/loyalty",
    exact: false,
    settingKey: "setting_loyalty",
    parentModule: null,
    title: "Loyalty Program",
  },
  {
    path: "/dashboard/settings/currencies",
    exact: false,
    settingKey: "setting_currencies",
    parentModule: null,
    title: "Currencies & Denominations",
  },
  {
    path: "/dashboard/settings/printers",
    exact: false,
    settingKey: "setting_printers",
    parentModule: null,
    title: "Printers Setup",
  },
];
